import { createApi } from '@reduxjs/toolkit/query/react'

import { escapeODataString } from '@/common/helpers/odataEscape'
import { baseQuery } from '@/core/api/baseQuery'
import { GenericResponseOData } from '@/core/api/interfaces/GenericResponseOData'

export interface CatalogoItem {
  ListaSeleccionId: number
  Nombre: string
  Valor: string
  Ambito: string
}

export interface EstadoItem {
  EstadoId: number
  Nombre: string
  [key: string]: unknown
}

export interface Geografia {
  Nombre: string
  [key: string]: unknown
}

export interface EmbarcacionItem {
  EmbarcacionId: number
  Nombre: string
  [key: string]: unknown
}

export interface TipoCabinaAllotment {
  TipoCabinaId: number
  DeckId: number
  Sigla: string
  CantidadCabinas: number
  [key: string]: unknown
}

// Catálogos de referencia (listas de selección, estados, geografías) compartidos entre módulos.
// Datos casi estáticos: se cachean 1h y se deduplican, evitando GET repetidos al abrir formularios.
//
// La caché se invalida por etiquetas, no por tiempo. Antes solo caducaba: el CRUD de estos
// catálogos no pasa por RTK Query —va por helpers HTTP y sagas—, así que nada avisaba de un alta o
// una baja. Con `refetchOnMountOrArgChange` en false, volver a abrir el formulario servía la
// entrada cacheada tal cual, y el temporizador se reiniciaba en cada desuscripción: en una sesión
// donde el formulario se abriera al menos una vez por hora, la lista NO se volvía a pedir nunca.
// Se veía el valor recién creado ausente, y el eliminado seguía ahí y se podía guardar.
//
// Las etiquetas llevan id: el ámbito en las listas de selección y la embarcación en los tipos de
// cabina. Editar un catálogo no tira los otros. Invalidar el tipo entero —sin id— sigue alcanzando
// a todos, que es la salida cuando el punto de escritura no sabe a cuál toca.
export const catalogosApi = createApi({
  reducerPath: 'catalogosApi',
  baseQuery,
  keepUnusedDataFor: 3600,
  tagTypes: ['ListaSeleccion', 'Estado', 'Geografia', 'Embarcacion', 'TipoCabina'],
  endpoints: (builder) => ({
    getListaSeleccionPorAmbito: builder.query<CatalogoItem[], string>({
      query: (ambito: string) =>
        `ListasSeleccion?$filter=Ambito eq '${escapeODataString(ambito)}'` +
        `&$select=ListaSeleccionId,Nombre,Valor,Ambito&$orderby=Nombre`,
      transformResponse: (response: GenericResponseOData<CatalogoItem[]>) => response.value ?? [],
      providesTags: (_resultado, _error, ambito) => [{ type: 'ListaSeleccion' as const, id: ambito }],
    }),
    getEstadosPorAmbito: builder.query<EstadoItem[], string>({
      query: (ambito: string) => `Estados?$filter=Ambito eq '${escapeODataString(ambito)}'`,
      transformResponse: (response: GenericResponseOData<EstadoItem[]>) => response.value ?? [],
      providesTags: (_resultado, _error, ambito) => [{ type: 'Estado' as const, id: ambito }],
    }),
    getGeografias: builder.query<Geografia[], void>({
      query: () => 'Geografias',
      transformResponse: (response: GenericResponseOData<Geografia[]>) => response.value ?? [],
      providesTags: ['Geografia'],
    }),
    // Flota y estructura de barcos: semi-estáticas pero con CRUD en Gestión de Embarcación,
    // por eso caché corta (5 min) en vez de la horaria de los catálogos puros
    getEmbarcaciones: builder.query<EmbarcacionItem[], void>({
      query: () => 'Embarcaciones',
      keepUnusedDataFor: 300,
      transformResponse: (response: GenericResponseOData<EmbarcacionItem[]>) => response.value ?? [],
      providesTags: ['Embarcacion'],
    }),
    getTipoCabinasPorEmbarcacion: builder.query<TipoCabinaAllotment[], number>({
      // Las tres opciones van con `$`: sin el prefijo OData las toma como opciones personalizadas y
      // las ignora en silencio, respondiendo 200. Sin el filtro, el grid de allotment listaba los
      // tipos de cabina de TODA la flota, y sin el expand la celda que pinta `Deck.Sigla + Sigla`
      // renderizaba literalmente `undefinedXX`. Los endpoints vecinos de este mismo archivo ya
      // usaban `$filter` correctamente.
      query: (embarcacionId: number) =>
        `TipoCabinas?$expand=Deck($expand=Embarcacion($select=EmbarcacionId,Nombre))` +
        `&$filter=Deck/EmbarcacionId eq ${embarcacionId}&$select=TipoCabinaId,DeckId,Sigla,CantidadCabinas`,
      keepUnusedDataFor: 300,
      transformResponse: (response: GenericResponseOData<TipoCabinaAllotment[]>) => response.value ?? [],
      providesTags: (_resultado, _error, embarcacionId) => [{ type: 'TipoCabina' as const, id: embarcacionId }],
    }),
  }),
})

export const {
  useGetListaSeleccionPorAmbitoQuery,
  useGetEstadosPorAmbitoQuery,
  useGetGeografiasQuery,
  useGetEmbarcacionesQuery,
  useGetTipoCabinasPorEmbarcacionQuery,
} = catalogosApi
